import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Wallet,
  ArrowUpRight,
  ArrowDownLeft,
  Activity,
  RefreshCw,
  Building2,
  CreditCard,
  Shield,
  AlertTriangle,
  FileText,
  Users,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  BarChart3,
  PieChart as PieChartIcon,
  Zap,
  Bell,
  Search,
  Download,
  Settings,
  AlertCircle,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button } from '../components/ui';
import { formatCurrency, formatCompactCurrency, formatDate } from '../lib/utils';
import { api } from '../lib/api';
import { ENDPOINTS } from '../config/api';
import { useAuthStore } from '../stores/auth.store';
import { useAnypayBalance, useAnypayTransactions } from '../hooks/useAnypay';
import {
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { useState, useMemo } from 'react';

// ============================================
// TYPES
// ============================================

interface Account {
  id: string;
  name: string;
  type: string;
  balance: string;
  status: string;
  pixKey?: string;
}

interface Transaction {
  id: string;
  type: string;
  amount: number;
  grossAmount?: string;
  netAmount?: string;
  totalFees?: string;
  status: string;
  description?: string;
  createdAt: string;
  fromAccount?: { name: string };
  toAccount?: { name: string };
}

interface AccountsSummary {
  totalBalance: string;
  bolsaoBalance: string;
  virtualBalance: string;
  realBalance: string;
  accountCount: { total: number; bolsao: number; virtual: number; real: number };
  accounts: Account[];
}

interface MyAccountData {
  hasAccount: boolean;
  account: Account | null;
}

interface AuditLog {
  id: string;
  action: string;
  resource: string;
  resourceId?: string;
  userId?: string;
  ipAddress?: string;
  createdAt: string;
}

// ============================================
// ANIMATION VARIANTS
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const pulseVariants = {
  pulse: {
    scale: [1, 1.02, 1],
    transition: { duration: 2, repeat: Infinity },
  },
};

// Helper to safely format currency in tooltips
const formatTooltipValue = (value: number | undefined) => formatCurrency(Number(value || 0));

// ============================================
// MAIN DASHBOARD ROUTER
// ============================================

export function Dashboard() {
  const { user } = useAuthStore();

  switch (user?.type) {
    case 'ADMIN':
      return <AdminDashboard />;
    case 'OPERATOR':
      return <OperatorDashboard />;
    case 'COMPLIANCE':
      return <ComplianceDashboard />;
    case 'EMPLOYEE':
      return <EmployeeDashboard />;
    case 'CUSTOMER':
    case 'SUPPLIER':
    default:
      return <CustomerDashboard />;
  }
}

// ============================================
// ADMIN DASHBOARD - Executive View
// ============================================

function AdminDashboard() {
  const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month'>('week');
  const { user, isSuperAdmin } = useAuthStore();
  const superAdmin = isSuperAdmin?.() ?? false;

  // Only fetch AnyPay data for super admin
  const { data: balance, isLoading: balanceLoading, refetch: refetchBalance } = useAnypayBalance();

  const { data: summaryData, isLoading: loadingSummary, refetch: refetchSummary } = useQuery({
    queryKey: ['accounts-summary'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.accounts.summary);
      return response.data as AccountsSummary;
    },
  });

  // Fetch real AnyPay transactions with summary (volume, fees, counts)
  // Only for super admin — admin tenant gets data from financial-summary
  const { data: anypayTxData, isLoading: loadingAnypayTx, refetch: refetchAnypayTx } = useAnypayTransactions({ limit: 100 });

  // Fetch financial summary for tenant admin (works for all admins)
  const { data: finSummary, isLoading: loadingFinSummary, refetch: refetchFinSummary } = useQuery({
    queryKey: ['financial-summary-dashboard'],
    queryFn: async () => {
      const response = await api.get('/admin/financial-summary');
      return response.data;
    },
    enabled: !superAdmin, // Only fetch for non-super admin
  });

  const summary = summaryData;
  const anypayTransactions = anypayTxData?.transactions || [];
  const anypayTxSummary = anypayTxData?.summary;
  const isLoading = loadingSummary || (superAdmin ? loadingAnypayTx : loadingFinSummary);

  const handleRefresh = () => {
    if (superAdmin) {
      refetchBalance();
      refetchAnypayTx();
    } else {
      refetchFinSummary();
    }
    refetchSummary();
  };

  // KPI values — different source depending on role
  // Super Admin: uses AnyPay data (escrow balance, gateway volume)
  // Admin Tenant: uses financial-summary data (correntistas, receita tenant)
  const finB = finSummary?.balancete;
  const finE = finSummary?.extrato;

  const totalVolume = superAdmin
    ? (anypayTxSummary?.totalReceivedConfirmed ?? 0)
    : parseInt(finE?.entradas?.depositosBrutos || '0', 10);
  const totalFees = superAdmin
    ? (anypayTxSummary?.totalFees ?? 0)
    : parseInt(finB?.investRedencao?.totalEmCaixa || '0', 10);
  const escrowBalance = anypayTxSummary?.escrowBalance ?? 0;
  const totalWithdrawn = anypayTxSummary?.totalWithdrawn ?? 0;
  const totalPending = anypayTxSummary?.totalPending ?? 0;
  const totalTxCount = superAdmin
    ? (anypayTxSummary?.totalCount ?? 0)
    : parseInt(finE?.entradas?.qtdDepositos || '0', 10);
  const totalReceived = anypayTxSummary?.totalReceived ?? 0;
  const successRate = totalReceived > 0 ? (totalVolume / totalReceived) * 100 : 100;

  // Saldo empresa: super admin vê escrow, admin tenant vê saldoEmpresa (AtivoPay + saques admin)
  const saldoEmpresa = superAdmin
    ? (balance ? balance.available : 0)
    : parseInt(finB?.saldoEmpresa || finB?.correntistas?.saldoPositivo || '0', 10);

  // Map AnyPay transactions to Transaction interface for shared components
  const transactions: Transaction[] = anypayTransactions.map(tx => ({
    id: tx.id,
    type: tx.type,
    amount: tx.grossAmount,
    grossAmount: String(tx.grossAmount),
    netAmount: String(tx.netAmount),
    totalFees: String(tx.totalFees),
    status: tx.status,
    description: tx.description,
    createdAt: tx.createdAt,
  }));

  // Chart data
  const pieData = summary ? [
    { name: 'Bolsão', value: Number(summary.bolsaoBalance), color: '#06b6d4' },
    { name: 'Virtual', value: Number(summary.virtualBalance), color: '#8b5cf6' },
    { name: 'Real', value: Number(summary.realBalance), color: '#10b981' },
  ].filter(d => d.value > 0) : [];

  // Calculate transaction trend from real AnyPay data
  const transactionTrend = useMemo(() => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      const dateStr = date.toDateString();
      
      const dayTransactions = transactions.filter(tx => {
        const txDate = new Date(tx.createdAt).toDateString();
        return txDate === dateStr;
      });
      
      const received = dayTransactions
        .filter(tx => tx.type === 'PIX_RECEIVE')
        .reduce((sum, tx) => sum + (tx.grossAmount ? Number(tx.grossAmount) : Number(tx.amount)), 0);
      
      const sent = dayTransactions
        .filter(tx => tx.type === 'PIX_SEND')
        .reduce((sum, tx) => sum + (tx.grossAmount ? Number(tx.grossAmount) : Number(tx.amount)), 0);
      
      return {
        date: date.toLocaleDateString('pt-BR', { weekday: 'short' }),
        received,
        sent,
      };
    });
    return last7Days;
  }, [transactions]);

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
            Dashboard Executivo
          </h1>
          <p className="text-surface-400 mt-1">Visão consolidada do seu negócio</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-surface-800 rounded-lg p-1">
            {(['today', 'week', 'month'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  timeRange === range
                    ? 'bg-primary-500 text-white'
                    : 'text-surface-400 hover:text-surface-200'
                }`}
              >
                {range === 'today' ? 'Hoje' : range === 'week' ? 'Semana' : 'Mês'}
              </button>
            ))}
          </div>
          <Button variant="secondary" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </motion.div>

      {/* KPI Cards */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Saldo Empresa"
          value={saldoEmpresa}
          icon={Wallet}
          color="primary"
          subtitle={superAdmin && balance ? `Pendente: ${formatCurrency(balance.pending)}` : `${finB?.correntistas?.comSaldo || 0} contas com saldo`}
          isLoading={superAdmin ? balanceLoading : loadingFinSummary}
        />
        <KPICard
          title="Volume Confirmado"
          value={totalVolume}
          icon={Activity}
          color="accent"
          subtitle={`${totalTxCount} transações`}
          isLoading={superAdmin ? loadingAnypayTx : loadingFinSummary}
        />
        <KPICard
          title={superAdmin ? "Taxas Arrecadadas" : "Receita em Caixa"}
          value={totalFees}
          icon={TrendingUp}
          color="green"
          subtitle={superAdmin ? `Líquido: ${formatCurrency(anypayTxSummary?.totalNet ?? 0)}` : `A receber: ${formatCurrency(parseInt(finB?.investRedencao?.totalAReceber || '0', 10))}`}
          isLoading={superAdmin ? loadingAnypayTx : loadingFinSummary}
        />
        <KPICard
          title="Taxa de Confirmação"
          value={successRate}
          icon={CheckCircle}
          color="cyan"
          subtitle={`Confirmado: ${formatCurrency(totalVolume)} de ${formatCurrency(totalReceived)}`}
          isPercentage
          isLoading={loadingAnypayTx}
        />
      </motion.div>

      {/* Charts Row */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Transaction Trend */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary-400" />
              Fluxo de Transações
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={transactionTrend}>
                  <defs>
                    <linearGradient id="colorReceived" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgb(15, 23, 42)',
                      border: '1px solid rgb(51, 65, 85)',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => formatTooltipValue(value as number)}
                  />
                  <Area type="monotone" dataKey="received" stroke="#10b981" fill="url(#colorReceived)" name="Recebido" />
                  <Area type="monotone" dataKey="sent" stroke="#ef4444" fill="url(#colorSent)" name="Enviado" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>

        {/* Balance Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-accent-400" />
              Distribuição de Saldo
            </CardTitle>
          </CardHeader>
          <CardBody>
            {pieData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatTooltipValue(value as number)}
                      contentStyle={{
                        backgroundColor: 'rgb(15, 23, 42)',
                        border: '1px solid rgb(51, 65, 85)',
                        borderRadius: '8px',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2">
                  {pieData.map((entry, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                      <span className="text-xs text-surface-400">{entry.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-surface-400">
                <p>Sem dados para exibir</p>
              </div>
            )}
          </CardBody>
        </Card>
      </motion.div>

      {/* Accounts & Transactions */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Accounts List */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary-400" />
              Contas por Tipo
            </CardTitle>
            <Badge variant="info">{summary?.accountCount.total || 0} contas</Badge>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <LoadingList count={4} />
            ) : !summary?.accounts.length ? (
              <EmptyState icon={Wallet} message="Nenhuma conta cadastrada" />
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {summary.accounts.map(account => (
                  <AccountRow key={account.id} account={account} />
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Recent Transactions */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-accent-400" />
              Transações Recentes
            </CardTitle>
            <Button variant="ghost" size="sm">Ver todas</Button>
          </CardHeader>
          <CardBody className="p-0">
            {isLoading ? (
              <div className="p-6"><LoadingList count={5} /></div>
            ) : transactions.length === 0 ? (
              <div className="p-6"><EmptyState icon={Activity} message="Nenhuma transação" /></div>
            ) : (
              <TransactionsList transactions={transactions.slice(0, 5)} />
            )}
          </CardBody>
        </Card>
      </motion.div>

      {/* Status Overview */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              Status das Transações
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {superAdmin && <StatusCard label="Saldo Escrow" value={escrowBalance} total={totalVolume} color="green" icon={CheckCircle} isCurrency />}
              {superAdmin && <StatusCard label="Saques Realizados" value={totalWithdrawn} total={totalVolume} color="cyan" icon={ArrowUpRight} isCurrency />}
              <StatusCard label="Pendente" value={totalPending} total={totalReceived} color="yellow" icon={Clock} isCurrency />
              <StatusCard label="Total Transações" value={totalTxCount} total={totalTxCount} color="primary" icon={Activity} />
            </div>
          </CardBody>
        </Card>
      </motion.div>
    </motion.div>
  );
}



// ============================================
// OPERATOR DASHBOARD - Operational View
// ============================================

function OperatorDashboard() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data: summaryData, isLoading: loadingSummary, refetch: refetchSummary } = useQuery({
    queryKey: ['accounts-summary'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.accounts.summary);
      return response.data as AccountsSummary;
    },
  });

  const { data: transactionsData, isLoading: loadingTransactions, refetch: refetchTransactions } = useQuery({
    queryKey: ['transactions-operator'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.transactions.list, { params: { limit: 50 } });
      return response.data as { transactions: Transaction[]; total: number };
    },
  });

  const summary = summaryData;
  const allTransactions = transactionsData?.transactions || [];
  const isLoading = loadingSummary || loadingTransactions;

  const handleRefresh = () => {
    refetchSummary();
    refetchTransactions();
  };

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return allTransactions.filter(tx => {
      const matchesSearch = searchTerm === '' || 
        tx.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.id.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || tx.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [allTransactions, searchTerm, statusFilter]);

  // Metrics
  const pendingCount = allTransactions.filter(tx => tx.status === 'PENDING').length;
  const processingCount = allTransactions.filter(tx => tx.status === 'PROCESSING').length;
  const todayCount = allTransactions.filter(tx => {
    const txDate = new Date(tx.createdAt).toDateString();
    return txDate === new Date().toDateString();
  }).length;

  const virtualAccounts = summary?.accounts.filter(a => a.type === 'VIRTUAL') || [];

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-accent-400 to-primary-400 bg-clip-text text-transparent">
            Painel Operacional
          </h1>
          <p className="text-surface-400 mt-1">Gestão de transações e contas virtuais</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button variant="primary">
            <Zap className="w-4 h-4 mr-2" />
            Nova Transação
          </Button>
        </div>
      </motion.div>

      {/* Quick Stats */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <QuickStatCard
          icon={Clock}
          label="Pendentes"
          value={pendingCount}
          color="yellow"
          alert={pendingCount > 10}
        />
        <QuickStatCard
          icon={Activity}
          label="Processando"
          value={processingCount}
          color="blue"
        />
        <QuickStatCard
          icon={Users}
          label="Contas Virtuais"
          value={virtualAccounts.length}
          color="purple"
        />
        <QuickStatCard
          icon={TrendingUp}
          label="Hoje"
          value={todayCount}
          color="green"
        />
      </motion.div>

      {/* Main Content */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Transactions Panel */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between w-full">
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-accent-400" />
                Transações
              </CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 pr-4 py-1.5 bg-surface-800 border border-surface-700 rounded-lg text-sm text-surface-200 placeholder-surface-500 focus:outline-none focus:border-primary-500"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-1.5 bg-surface-800 border border-surface-700 rounded-lg text-sm text-surface-200 focus:outline-none focus:border-primary-500"
                >
                  <option value="all">Todos</option>
                  <option value="PENDING">Pendentes</option>
                  <option value="PROCESSING">Processando</option>
                  <option value="COMPLETED">Completadas</option>
                  <option value="FAILED">Falharam</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {isLoading ? (
              <div className="p-6"><LoadingList count={8} /></div>
            ) : filteredTransactions.length === 0 ? (
              <div className="p-6"><EmptyState icon={Activity} message="Nenhuma transação encontrada" /></div>
            ) : (
              <div className="max-h-[500px] overflow-y-auto">
                <TransactionsList transactions={filteredTransactions.slice(0, 15)} showActions />
              </div>
            )}
          </CardBody>
        </Card>

        {/* Virtual Accounts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-purple-400" />
              Contas Virtuais
            </CardTitle>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <LoadingList count={5} />
            ) : virtualAccounts.length === 0 ? (
              <EmptyState icon={CreditCard} message="Nenhuma conta virtual" />
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {virtualAccounts.map(account => (
                  <div
                    key={account.id}
                    className="p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-surface-100">{account.name}</p>
                        <p className="text-xs text-surface-500">
                          {account.pixKey ? `Pix: ${account.pixKey.slice(0, 8)}...` : 'Sem chave Pix'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-surface-100">
                          {formatCurrency(Number(account.balance))}
                        </p>
                        <Badge variant={account.status === 'ACTIVE' ? 'success' : 'warning'} size="sm">
                          {account.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </motion.div>

      {/* Pending Actions */}
      {pendingCount > 0 && (
        <motion.div variants={itemVariants}>
          <Card className="border-yellow-500/30 bg-yellow-500/5">
            <CardBody>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-yellow-500/20">
                  <AlertTriangle className="w-6 h-6 text-yellow-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-surface-100">Ações Pendentes</h3>
                  <p className="text-sm text-surface-400">
                    Existem {pendingCount} transações aguardando processamento
                  </p>
                </div>
                <Button variant="primary" size="sm">
                  Processar Agora
                </Button>
              </div>
            </CardBody>
          </Card>
        </motion.div>
      )}
    </motion.div>
  );
}

// ============================================
// COMPLIANCE DASHBOARD - Audit View
// ============================================

function ComplianceDashboard() {
  const [dateRange] = useState<'today' | 'week' | 'month'>('week');

  const { data: auditData, isLoading: loadingAudit, refetch: refetchAudit } = useQuery({
    queryKey: ['audit-logs', dateRange],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.audit.logs, { params: { limit: 50 } });
      return response.data as { logs: AuditLog[]; total: number };
    },
  });

  const { data: transactionsData, isLoading: loadingTransactions } = useQuery({
    queryKey: ['transactions-compliance'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.transactions.list, { params: { limit: 100 } });
      return response.data as { transactions: Transaction[]; total: number };
    },
  });

  const auditLogs = auditData?.logs || [];
  const transactions = transactionsData?.transactions || [];
  const isLoading = loadingAudit || loadingTransactions;

  // Risk Analysis
  const failedTransactions = transactions.filter(tx => tx.status === 'FAILED');
  const highValueTransactions = transactions.filter(tx => {
    const amount = tx.grossAmount ? Number(tx.grossAmount) : Number(tx.amount);
    return amount > 1000000; // > R$ 10.000
  });
  const suspiciousActivities = auditLogs.filter(log => 
    log.action.includes('DELETE') || log.action.includes('UPDATE')
  );

  // Risk Score (simplified)
  const riskScore = Math.min(100, 
    (failedTransactions.length * 5) + 
    (highValueTransactions.length * 2) + 
    (suspiciousActivities.length * 3)
  );

  const getRiskLevel = (score: number) => {
    if (score < 20) return { label: 'Baixo', color: 'green' };
    if (score < 50) return { label: 'Médio', color: 'yellow' };
    return { label: 'Alto', color: 'red' };
  };

  const riskLevel = getRiskLevel(riskScore);

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
            Painel de Compliance
          </h1>
          <p className="text-surface-400 mt-1">Auditoria, conformidade e análise de riscos</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={() => refetchAudit()} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button variant="primary">
            <Download className="w-4 h-4 mr-2" />
            Exportar Relatório
          </Button>
        </div>
      </motion.div>

      {/* Risk Overview */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`border-${riskLevel.color}-500/30`}>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl bg-${riskLevel.color}-500/20`}>
                <Shield className={`w-6 h-6 text-${riskLevel.color}-400`} />
              </div>
              <div>
                <p className="text-sm text-surface-400">Nível de Risco</p>
                <p className={`text-2xl font-bold text-${riskLevel.color}-400`}>{riskLevel.label}</p>
                <p className="text-xs text-surface-500">Score: {riskScore}/100</p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-red-500/20">
                <XCircle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Transações Falharam</p>
                <p className="text-2xl font-bold text-red-400">{failedTransactions.length}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-yellow-500/20">
                <AlertTriangle className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Alto Valor</p>
                <p className="text-2xl font-bold text-yellow-400">{highValueTransactions.length}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-purple-500/20">
                <Eye className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Logs de Auditoria</p>
                <p className="text-2xl font-bold text-purple-400">{auditLogs.length}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      </motion.div>

      {/* Main Content */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Audit Logs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-400" />
              Logs de Auditoria
            </CardTitle>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <LoadingList count={6} />
            ) : auditLogs.length === 0 ? (
              <EmptyState icon={FileText} message="Nenhum log encontrado" />
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {auditLogs.slice(0, 10).map(log => (
                  <div
                    key={log.id}
                    className="p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant={
                              log.action.includes('DELETE') ? 'error' :
                              log.action.includes('UPDATE') ? 'warning' :
                              'info'
                            } 
                            size="sm"
                          >
                            {log.action}
                          </Badge>
                          <span className="text-sm text-surface-300">{log.resource}</span>
                        </div>
                        <p className="text-xs text-surface-500 mt-1">
                          {log.ipAddress && `IP: ${log.ipAddress} • `}
                          {formatDate(log.createdAt)}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Suspicious Transactions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              Transações Suspeitas
            </CardTitle>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <LoadingList count={6} />
            ) : highValueTransactions.length === 0 && failedTransactions.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                <p className="text-surface-400">Nenhuma atividade suspeita</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {[...failedTransactions, ...highValueTransactions].slice(0, 10).map(tx => (
                  <div
                    key={tx.id}
                    className="p-3 rounded-lg bg-surface-800/50 border border-red-500/20"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant={tx.status === 'FAILED' ? 'error' : 'warning'} size="sm">
                            {tx.status === 'FAILED' ? 'Falhou' : 'Alto Valor'}
                          </Badge>
                          <span className="text-sm text-surface-300">{tx.type}</span>
                        </div>
                        <p className="text-xs text-surface-500 mt-1">
                          {formatDate(tx.createdAt)}
                        </p>
                      </div>
                      <p className="font-semibold text-surface-100">
                        {formatCurrency(tx.grossAmount ? Number(tx.grossAmount) : Number(tx.amount))}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </motion.div>

      {/* Compliance Alerts */}
      <motion.div variants={itemVariants}>
        <Card className="border-orange-500/30 bg-orange-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-400" />
              Alertas de Compliance
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              {failedTransactions.length > 5 && (
                <AlertItem
                  type="error"
                  title="Alta taxa de falhas"
                  description={`${failedTransactions.length} transações falharam recentemente`}
                />
              )}
              {highValueTransactions.length > 3 && (
                <AlertItem
                  type="warning"
                  title="Transações de alto valor"
                  description={`${highValueTransactions.length} transações acima de R$ 10.000`}
                />
              )}
              {suspiciousActivities.length > 10 && (
                <AlertItem
                  type="warning"
                  title="Atividade incomum"
                  description={`${suspiciousActivities.length} alterações/exclusões detectadas`}
                />
              )}
              {failedTransactions.length <= 5 && highValueTransactions.length <= 3 && suspiciousActivities.length <= 10 && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <p className="text-surface-300">Nenhum alerta ativo no momento</p>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </motion.div>
    </motion.div>
  );
}


// ============================================
// EMPLOYEE DASHBOARD - Limited View
// ============================================

function EmployeeDashboard() {
  const { data: summaryData, isLoading: loadingSummary, refetch } = useQuery({
    queryKey: ['accounts-summary'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.accounts.summary);
      return response.data as AccountsSummary;
    },
  });

  const { data: transactionsData, isLoading: loadingTransactions } = useQuery({
    queryKey: ['transactions-employee'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.transactions.list, { params: { limit: 20 } });
      return response.data as { transactions: Transaction[]; total: number };
    },
  });

  const summary = summaryData;
  const transactions = transactionsData?.transactions || [];
  const isLoading = loadingSummary || loadingTransactions;

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            Painel do Funcionário
          </h1>
          <p className="text-surface-400 mt-1">Visão geral das operações</p>
        </div>
        <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </motion.div>

      {/* Stats */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <KPICard
          title="Total de Contas"
          value={summary?.accountCount.total || 0}
          icon={Wallet}
          color="primary"
          subtitle="Contas ativas"
          isLoading={isLoading}
          isCurrency={false}
        />
        <KPICard
          title="Contas Virtuais"
          value={summary?.accountCount.virtual || 0}
          icon={CreditCard}
          color="accent"
          subtitle="Correntistas"
          isLoading={isLoading}
          isCurrency={false}
        />
        <KPICard
          title="Transações Hoje"
          value={transactions.filter(tx => {
            const txDate = new Date(tx.createdAt).toDateString();
            return txDate === new Date().toDateString();
          }).length}
          icon={Activity}
          color="green"
          subtitle="Movimentações"
          isLoading={isLoading}
          isCurrency={false}
        />
      </motion.div>

      {/* Content */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Accounts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary-400" />
              Contas do Sistema
            </CardTitle>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <LoadingList count={5} />
            ) : !summary?.accounts.length ? (
              <EmptyState icon={Wallet} message="Nenhuma conta" />
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {summary.accounts.map(account => (
                  <AccountRow key={account.id} account={account} compact />
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Transactions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-accent-400" />
              Últimas Transações
            </CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            {isLoading ? (
              <div className="p-6"><LoadingList count={5} /></div>
            ) : transactions.length === 0 ? (
              <div className="p-6"><EmptyState icon={Activity} message="Nenhuma transação" /></div>
            ) : (
              <TransactionsList transactions={transactions.slice(0, 8)} />
            )}
          </CardBody>
        </Card>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// CUSTOMER DASHBOARD - Personal View
// ============================================

function CustomerDashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const { data: myAccountData, isLoading: loadingAccount, refetch: refetchAccount } = useQuery({
    queryKey: ['my-account'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.accounts.myAccount);
      return response.data as MyAccountData;
    },
  });

  const { data: myStatementData, isLoading: loadingStatement, refetch: refetchStatement } = useQuery({
    queryKey: ['my-statement'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.statements.myStatement, {
        params: { limit: 20 },
      });
      return response.data;
    },
    enabled: !!myAccountData?.hasAccount,
  });

  const isLoading = loadingAccount || loadingStatement;
  const account = myAccountData?.account;
  const transactions = myStatementData?.data || [];
  const summary = myStatementData?.summary;

  const handleRefresh = () => {
    refetchAccount();
    refetchStatement();
  };

  if (!myAccountData?.hasAccount) {
    return (
      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
        <motion.div variants={itemVariants}>
          <Card className="border-yellow-500/30">
            <CardBody className="text-center py-16">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Wallet className="w-20 h-20 mx-auto mb-6 text-surface-500" />
              </motion.div>
              <h2 className="text-2xl font-bold text-surface-100 mb-3">
                Conta em Configuração
              </h2>
              <p className="text-surface-400 mb-6 max-w-md mx-auto">
                Sua conta virtual está sendo preparada. Em breve você terá acesso completo 
                ao seu saldo e extrato.
              </p>
              <div className="flex items-center justify-center gap-2 text-yellow-400">
                <Clock className="w-5 h-5" />
                <span>Aguardando ativação</span>
              </div>
            </CardBody>
          </Card>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
            Minha Conta
          </h1>
          <p className="text-surface-400 mt-1">Olá, {user?.name}! 👋</p>
        </div>
        <Button variant="secondary" onClick={handleRefresh} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </motion.div>

      {/* Balance Card */}
      <motion.div variants={itemVariants}>
        <motion.div variants={pulseVariants} animate="pulse">
          <Card variant="glass" className="overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-accent-500/10" />
            <CardBody className="relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-surface-400 mb-1">Saldo Disponível</p>
                  <p className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 truncate">
                    {isLoading ? (
                      <span className="animate-pulse bg-surface-700 rounded h-14 w-56 block" />
                    ) : (
                      formatCurrency(Number(account?.balance || 0))
                    )}
                  </p>
                  <div className="flex items-center gap-6 mt-6">
                    <div>
                      <p className="text-xs text-surface-500">Conta</p>
                      <p className="text-sm font-medium text-surface-300">{account?.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-surface-500">Status</p>
                      <Badge variant={account?.status === 'ACTIVE' ? 'success' : 'warning'}>
                        {account?.status === 'ACTIVE' ? 'Ativa' : account?.status}
                      </Badge>
                    </div>
                    {account?.pixKey && (
                      <div>
                        <p className="text-xs text-surface-500">Chave Pix</p>
                        <p className="text-sm font-mono text-primary-400">{account.pixKey}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-6 rounded-3xl bg-gradient-to-br from-primary-500/20 to-accent-500/20">
                  <Wallet className="w-16 h-16 text-primary-400" />
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <QuickActionCard icon={ArrowUpRight} label="Enviar Pix" color="red" onClick={() => navigate('/banking/customer/pix')} />
        <QuickActionCard icon={ArrowDownLeft} label="Receber Pix" color="green" onClick={() => navigate('/banking/customer/pix')} />
        <QuickActionCard icon={FileText} label="Extrato" color="blue" onClick={() => navigate('/banking/customer/statement')} />
        <QuickActionCard icon={Settings} label="Configurações" color="purple" onClick={() => navigate('/banking/settings/account')} />
      </motion.div>

      {/* Stats */}
      {summary && (
        <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card variant="stat" hover>
            <CardBody>
              <div className="flex items-center gap-4 min-w-0">
                <div className="p-3 rounded-xl bg-green-500/20 flex-shrink-0">
                  <ArrowDownLeft className="w-6 h-6 text-green-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-surface-400">Total Recebido</p>
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-400 truncate" title={formatCurrency(Number(summary.totalReceivedNet || summary.totalReceived || 0))}>
                    {formatCompactCurrency(Number(summary.totalReceivedNet || summary.totalReceived || 0))}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card variant="stat" hover>
            <CardBody>
              <div className="flex items-center gap-4 min-w-0">
                <div className="p-3 rounded-xl bg-red-500/20 flex-shrink-0">
                  <ArrowUpRight className="w-6 h-6 text-red-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-surface-400">Total Enviado</p>
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-red-400 truncate" title={formatCurrency(Number(summary.totalSentGross || summary.totalSent || 0))}>
                    {formatCompactCurrency(Number(summary.totalSentGross || summary.totalSent || 0))}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card variant="stat" hover>
            <CardBody>
              <div className="flex items-center gap-4 min-w-0">
                <div className="p-3 rounded-xl bg-yellow-500/20 flex-shrink-0">
                  <Activity className="w-6 h-6 text-yellow-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-surface-400">Taxas Pagas</p>
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-yellow-400 truncate" title={formatCurrency(Number(summary.totalFees || 0))}>
                    {formatCompactCurrency(Number(summary.totalFees || 0))}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>
      )}

      {/* Transactions */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary-400" />
              Minhas Transações
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/banking/customer/statement')}>Ver extrato completo</Button>
          </CardHeader>
          <CardBody className="p-0">
            {isLoading ? (
              <div className="p-6"><LoadingList count={5} /></div>
            ) : transactions.length === 0 ? (
              <div className="p-6">
                <EmptyState 
                  icon={Activity} 
                  message="Nenhuma transação ainda" 
                  description="Suas movimentações aparecerão aqui"
                />
              </div>
            ) : (
              <TransactionsList transactions={transactions.slice(0, 10)} />
            )}
          </CardBody>
        </Card>
      </motion.div>
    </motion.div>
  );
}


// ============================================
// SHARED COMPONENTS
// ============================================

interface KPICardProps {
  title: string;
  value: number;
  icon: React.ElementType;
  color: 'primary' | 'accent' | 'green' | 'yellow' | 'red' | 'cyan' | 'purple' | 'blue';
  subtitle?: string;
  trend?: number;
  isPercentage?: boolean;
  isCurrency?: boolean;
  isLoading?: boolean;
}

function KPICard({ 
  title, 
  value, 
  icon: Icon, 
  color, 
  subtitle, 
  trend, 
  isPercentage = false,
  isCurrency = true,
  isLoading = false 
}: KPICardProps) {
  const colorClasses = {
    primary: 'bg-primary-500/20 text-primary-400',
    accent: 'bg-accent-500/20 text-accent-400',
    green: 'bg-green-500/20 text-green-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    red: 'bg-red-500/20 text-red-400',
    cyan: 'bg-cyan-500/20 text-cyan-400',
    purple: 'bg-purple-500/20 text-purple-400',
    blue: 'bg-blue-500/20 text-blue-400',
  };

  return (
    <Card variant="stat" hover>
      <CardBody>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-surface-400 truncate">{title}</p>
            <p className="text-base sm:text-lg lg:text-xl font-bold text-surface-100 mt-1" title={
              isPercentage ? `${value.toFixed(1)}%` : isCurrency ? formatCurrency(value) : value.toLocaleString('pt-BR')
            }>
              {isLoading ? (
                <span className="animate-pulse bg-surface-700 rounded h-8 w-24 block" />
              ) : isPercentage ? (
                `${value.toFixed(1)}%`
              ) : isCurrency ? (
                formatCurrency(value)
              ) : (
                value.toLocaleString('pt-BR')
              )}
            </p>
            <div className="flex items-center gap-2 mt-2 min-w-0">
              {subtitle && <p className="text-xs text-surface-500 truncate" title={subtitle}>{subtitle}</p>}
              {trend !== undefined && (
                <span className={`text-xs flex items-center gap-1 flex-shrink-0 ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {Math.abs(trend).toFixed(1)}%
                </span>
              )}
            </div>
          </div>
          <div className={`p-3 rounded-xl flex-shrink-0 ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

interface QuickStatCardProps {
  icon: React.ElementType;
  label: string;
  value: number;
  color: 'yellow' | 'blue' | 'purple' | 'green' | 'red';
  alert?: boolean;
}

function QuickStatCard({ icon: Icon, label, value, color, alert }: QuickStatCardProps) {
  const colorClasses = {
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    red: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  return (
    <Card className={alert ? 'border-red-500/50 animate-pulse' : ''}>
      <CardBody>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-surface-500">{label}</p>
            <p className="text-xl font-bold text-surface-100">{value}</p>
          </div>
          {alert && (
            <AlertTriangle className="w-4 h-4 text-red-400 ml-auto" />
          )}
        </div>
      </CardBody>
    </Card>
  );
}

interface QuickActionCardProps {
  icon: React.ElementType;
  label: string;
  color: 'red' | 'green' | 'blue' | 'purple';
  onClick?: () => void;
}

function QuickActionCard({ icon: Icon, label, color, onClick }: QuickActionCardProps) {
  const colorClasses = {
    red: 'hover:bg-red-500/10 hover:border-red-500/30 group-hover:text-red-400',
    green: 'hover:bg-green-500/10 hover:border-green-500/30 group-hover:text-green-400',
    blue: 'hover:bg-blue-500/10 hover:border-blue-500/30 group-hover:text-blue-400',
    purple: 'hover:bg-purple-500/10 hover:border-purple-500/30 group-hover:text-purple-400',
  };

  return (
    <Card className={`cursor-pointer transition-all group ${colorClasses[color]}`} onClick={onClick}>
      <CardBody className="text-center py-6">
        <Icon className={`w-8 h-8 mx-auto mb-2 text-surface-400 transition-colors ${
          color === 'red' ? 'group-hover:text-red-400' :
          color === 'green' ? 'group-hover:text-green-400' :
          color === 'blue' ? 'group-hover:text-blue-400' :
          'group-hover:text-purple-400'
        }`} />
        <p className="text-sm font-medium text-surface-300">{label}</p>
      </CardBody>
    </Card>
  );
}

interface StatusCardProps {
  label: string;
  value: number;
  total: number;
  color: 'green' | 'yellow' | 'red' | 'primary' | 'cyan';
  icon: React.ElementType;
  isCurrency?: boolean;
}

function StatusCard({ label, value, total, color, icon: Icon, isCurrency }: StatusCardProps) {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  const colorClasses = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    primary: 'bg-primary-500',
    cyan: 'bg-cyan-500',
  };

  return (
    <div className="p-4 rounded-lg bg-surface-800/50 min-w-0">
      <div className="flex items-center gap-3 mb-3">
        <Icon className={`w-5 h-5 flex-shrink-0 ${
          color === 'green' ? 'text-green-400' :
          color === 'yellow' ? 'text-yellow-400' :
          color === 'red' ? 'text-red-400' :
          color === 'cyan' ? 'text-cyan-400' :
          'text-primary-400'
        }`} />
        <span className="text-sm text-surface-400 truncate">{label}</span>
      </div>
      <p className="text-lg sm:text-xl lg:text-2xl font-bold text-surface-100 mb-2 truncate" title={
        isCurrency ? formatCurrency(value) : String(value)
      }>
        {isCurrency ? formatCompactCurrency(value) : value}
      </p>
      <div className="w-full h-2 bg-surface-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${colorClasses[color]} transition-all duration-500`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
}

interface AccountRowProps {
  account: Account;
  compact?: boolean;
}

function AccountRow({ account, compact }: AccountRowProps) {
  const typeColors = {
    BOLSAO: 'bg-primary-500/20 text-primary-400',
    VIRTUAL: 'bg-accent-500/20 text-accent-400',
    REAL: 'bg-green-500/20 text-green-400',
  };

  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${typeColors[account.type as keyof typeof typeColors] || 'bg-surface-700'}`}>
          <Wallet className="w-4 h-4" />
        </div>
        <div>
          <p className="font-medium text-surface-100">{account.name}</p>
          {!compact && <p className="text-xs text-surface-500">{account.type}</p>}
        </div>
      </div>
      <div className="text-right">
        <p className="font-semibold text-surface-100">
          {formatCurrency(Number(account.balance))}
        </p>
        {!compact && (
          <Badge variant={account.status === 'ACTIVE' ? 'success' : 'warning'} size="sm">
            {account.status}
          </Badge>
        )}
      </div>
    </div>
  );
}

interface TransactionsListProps {
  transactions: Transaction[];
  showActions?: boolean;
}

function TransactionsList({ transactions, showActions }: TransactionsListProps) {
  return (
    <div className="divide-y divide-surface-800">
      {transactions.map(tx => {
        const isReceive = ['PIX_RECEIVE', 'BOLETO', 'CARD_PAYMENT'].includes(tx.type);
        const amount = tx.grossAmount ? Number(tx.grossAmount) : Number(tx.amount);
        
        return (
          <div
            key={tx.id}
            className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className={`p-2 rounded-lg ${
                isReceive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
                {isReceive ? <ArrowDownLeft className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
              </div>
              <div>
                <p className="font-medium text-surface-100">
                  {tx.description || tx.type.replace(/_/g, ' ')}
                </p>
                <p className="text-sm text-surface-500">
                  {tx.type.replace(/_/g, ' ')} • {formatDate(tx.createdAt)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className={`font-semibold ${isReceive ? 'text-green-400' : 'text-red-400'}`}>
                  {isReceive ? '+' : '-'}{formatCurrency(amount)}
                </p>
                <Badge
                  variant={tx.status === 'COMPLETED' ? 'success' : tx.status === 'PENDING' ? 'warning' : 'error'}
                  size="sm"
                >
                  {tx.status}
                </Badge>
              </div>
              {showActions && (
                <Button variant="ghost" size="sm">
                  <Eye className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface AlertItemProps {
  type: 'error' | 'warning' | 'info';
  title: string;
  description: string;
}

function AlertItem({ type, title, description }: AlertItemProps) {
  const styles = {
    error: 'bg-red-500/10 border-red-500/20 text-red-400',
    warning: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400',
    info: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  };

  const icons = {
    error: XCircle,
    warning: AlertTriangle,
    info: AlertCircle,
  };

  const Icon = icons[type];

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${styles[type]}`}>
      <Icon className="w-5 h-5 flex-shrink-0" />
      <div>
        <p className="font-medium text-surface-100">{title}</p>
        <p className="text-sm text-surface-400">{description}</p>
      </div>
    </div>
  );
}

interface EmptyStateProps {
  icon: React.ElementType;
  message: string;
  description?: string;
}

function EmptyState({ icon: Icon, message, description }: EmptyStateProps) {
  return (
    <div className="text-center py-8 text-surface-400">
      <Icon className="w-12 h-12 mx-auto mb-2 opacity-50" />
      <p>{message}</p>
      {description && <p className="text-sm text-surface-500 mt-1">{description}</p>}
    </div>
  );
}

function LoadingList({ count }: { count: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="animate-pulse bg-surface-800 rounded h-16" />
      ))}
    </div>
  );
}
